import React, { useState, useRef, useEffect } from 'react';
import { Task, FeedEntry } from './Interfaces';
import { doc, updateDoc, arrayUnion, Timestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../firebase';
import { formatDistanceToNow } from 'date-fns';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Modal, Button } from 'react-bootstrap';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

/** Escape a string for safe interpolation into HTML markup (e.g. a filename
 *  spliced into a feed entry's content string). */
function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

interface Props {
  task: Task | null;
  show: boolean;
  onHide: () => void;
  onTaskUpdated?: (updatedTask: Task) => void;
}

const TaskOverlayModal: React.FC<Props> = ({ task, show, onHide, onTaskUpdated }): JSX.Element => {
  const [feedContent, setFeedContent] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [feedEntryToDelete, setFeedEntryToDelete] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const feedEndRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<any>(null);

  // Auto-scroll to bottom when new feed entries are added
  useEffect(() => {
    if (feedEndRef.current && !showInput) { // Only scroll when input is not showing
      feedEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [task?.feed, showInput]);

  // Handle Bootstrap modal focus interference
  useEffect(() => {
    if (showInput) {
      // Disable Bootstrap's modal focus management temporarily
      const modalElement = document.querySelector('.modal.show');
      const originalTabIndex = modalElement?.getAttribute('tabindex');
      
      if (modalElement) {
        modalElement.removeAttribute('tabindex');
      }
      
      // Prevent Bootstrap from stealing focus back
      const preventFocusSteal = (e: FocusEvent) => {
        if (e.target === document.body && quillRef.current && showInput) {
          e.preventDefault();
          e.stopPropagation();
          // Focus the ReactQuill editor
          const editor = quillRef.current.getEditor();
          if (editor) {
            editor.focus();
          }
        }
      };
      
      document.addEventListener('focusin', preventFocusSteal, true);
      
      // Give the input time to render, then try to focus
      setTimeout(() => {
        if (quillRef.current) {
          const editor = quillRef.current.getEditor();
          if (editor) {
            editor.focus();
          }
        }
      }, 100);
      
      // Cleanup when input is hidden
      return () => {
        document.removeEventListener('focusin', preventFocusSteal, true);
        if (modalElement && originalTabIndex) {
          modalElement.setAttribute('tabindex', originalTabIndex);
        }
      };
    }
  }, [showInput]);

  const formatDate = (timestamp: any) => {
    try {
      let date: Date;
      
      // Handle null, undefined, or empty values
      if (!timestamp) {
        return 'No date';
      }
      
      // Handle Firestore Timestamp objects
      if (timestamp?.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      // Handle JavaScript Date objects
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      // Handle numeric timestamps (milliseconds)
      else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      }
      // Handle string timestamps
      else if (typeof timestamp === 'string') {
        // Handle ISO strings and other common formats
        const parsedDate = new Date(timestamp);
        if (isNaN(parsedDate.getTime())) {
          return 'Invalid date format';
        }
        date = parsedDate;
      }
      // Handle objects with seconds/nanoseconds (Firestore format)
      else if (timestamp?.seconds) {
        date = new Date(timestamp.seconds * 1000);
      }
      // Handle any other object format
      else if (typeof timestamp === 'object') {
        // Try to extract common timestamp properties
        if (timestamp.toISOString) {
          date = new Date(timestamp.toISOString());
        } else if (timestamp._seconds) {
          date = new Date(timestamp._seconds * 1000);
        } else {
          return 'Unknown date format';
        }
      }
      else {
        return 'Unsupported date type';
      }
      
      // Validate the final date
      if (!date || isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      console.error('Error formatting date:', error, 'timestamp:', timestamp);
      return 'Date error';
    }
  };

  const handleAddFeedEntry = async () => {
    if (!task || (!feedContent.trim() && selectedFiles.length === 0)) return;

    setIsSaving(true);
    setIsUploading(true);
    try {
      // Add text update if there's content
      if (feedContent.trim()) {
        const newFeedEntry: FeedEntry = {
          id: `${Date.now()}`,
          content: feedContent,
          timestamp: Timestamp.now(),
          type: 'update',
          source: 'human',
          info: 'Task update'
        };

        const taskRef = doc(db, 'tasks', task.id);
        await updateDoc(taskRef, {
          feed: arrayUnion(newFeedEntry),
          lastEdited: new Date().toISOString()
        });

        // Update local task state
        const updatedTask = {
          ...task,
          feed: [...(task.feed || []), newFeedEntry],
          lastEdited: new Date().toISOString()
        };

        if (onTaskUpdated) {
          onTaskUpdated(updatedTask);
        }
      }

      // Add file uploads if there are files
      if (selectedFiles.length > 0) {
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          const fileId = `${Date.now()}-${i}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
          const storageRef = ref(storage, `tasks/${task.id}/${fileId}`);
          
          try {
            const uploadTask = uploadBytesResumable(storageRef, file);

            // Track upload progress
            uploadTask.on('state_changed', 
              (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(prev => ({ ...prev, [`file_${i}`]: progress }));
              },
              (error) => {
                console.error(`Error uploading file ${file.name}:`, error);
                setUploadProgress(prev => {
                  const newProgress = { ...prev };
                  delete newProgress[`file_${i}`];
                  return newProgress;
                });
              },
              async () => {
                // Upload completed successfully
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                
                const isImage = file.type.startsWith('image/');
                const safeName = escapeHtml(file.name);
                const content = isImage
                  ? `📎 Attached image: <strong>${safeName}</strong>`
                  : `📎 Attached file: <strong>${safeName}</strong>`;

                const newFeedEntry: FeedEntry = {
                  id: fileId,
                  content: content,
                  timestamp: Timestamp.now(),
                  type: 'file',
                  source: 'human',
                  info: 'File attachment',
                  fileUrl: downloadURL,
                  filePath: `tasks/${task.id}/${fileId}`,
                  attachment: {
                    name: file.name,
                    size: file.size,
                    type: file.type
                  }
                };

                const taskRef = doc(db, 'tasks', task.id);
                await updateDoc(taskRef, {
                  feed: arrayUnion(newFeedEntry),
                  lastEdited: new Date().toISOString()
                });

                // Update local task state
                const updatedTask = {
                  ...task,
                  feed: [...(task.feed || []), newFeedEntry],
                  lastEdited: new Date().toISOString()
                };

                if (onTaskUpdated) {
                  onTaskUpdated(updatedTask);
                }

                // Clean up progress tracking
                setUploadProgress(prev => {
                  const newProgress = { ...prev };
                  delete newProgress[`file_${i}`];
                  return newProgress;
                });
              }
            );
          } catch (error) {
            console.error(`Error uploading file ${file.name}:`, error);
            setUploadProgress(prev => {
              const newProgress = { ...prev };
              delete newProgress[`file_${i}`];
              return newProgress;
            });
          }
        }
      }

      setFeedContent('');
      setSelectedFiles([]);
      setShowInput(false);

      // Success notification - simple approach without Bootstrap Toast
      const notification = document.createElement('div');
      notification.className = 'alert alert-success position-fixed top-0 end-0 m-3';
      notification.style.zIndex = '9999';
      notification.style.minWidth = '300px';
      notification.innerHTML = `
        <div class="d-flex align-items-center">
          <i class="bi bi-check-circle me-2"></i>
          <span>Task update saved successfully!</span>
          <button type="button" class="btn-close ms-auto" onclick="this.parentElement.parentElement.remove()"></button>
        </div>
      `;
      document.body.appendChild(notification);
      
      // Auto-remove after 3 seconds
      setTimeout(() => {
        if (notification.parentNode) {
          document.body.removeChild(notification);
        }
      }, 3000);

    } catch (error) {
      console.error('Error adding feed entry:', error);
      
      // Error notification - simple approach without Bootstrap Toast
      const notification = document.createElement('div');
      notification.className = 'alert alert-danger position-fixed top-0 end-0 m-3';
      notification.style.zIndex = '9999';
      notification.style.minWidth = '300px';
      notification.innerHTML = `
        <div class="d-flex align-items-center">
          <i class="bi bi-exclamation-triangle me-2"></i>
          <span>Failed to save task update</span>
          <button type="button" class="btn-close ms-auto" onclick="this.parentElement.parentElement.remove()"></button>
        </div>
      `;
      document.body.appendChild(notification);
      
      // Auto-remove after 5 seconds (longer for errors)
      setTimeout(() => {
        if (notification.parentNode) {
          document.body.removeChild(notification);
        }
      }, 5000);
    } finally {
      setIsSaving(false);
      setIsUploading(false);
      setUploadProgress({});
    }
  };

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setSelectedFiles(Array.from(files));
    }
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteFeedEntry = (feedEntryId: string) => {
    setFeedEntryToDelete(feedEntryId);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteFeedEntry = async () => {
    if (!task || !feedEntryToDelete) return;

    try {
      // Remove the feed entry from the task's feed array
      const updatedFeed = task.feed?.filter(entry => entry.id !== feedEntryToDelete) || [];
      
      const taskRef = doc(db, 'tasks', task.id);
      await updateDoc(taskRef, {
        feed: updatedFeed,
        lastEdited: new Date().toISOString()
      });

      // Update local task state
      const updatedTask = {
        ...task,
        feed: updatedFeed,
        lastEdited: new Date().toISOString()
      };

      if (onTaskUpdated) {
        onTaskUpdated(updatedTask);
      }

      // Success notification
      const notification = document.createElement('div');
      notification.className = 'alert alert-success position-fixed top-0 end-0 m-3';
      notification.style.zIndex = '9999';
      notification.style.minWidth = '300px';
      notification.innerHTML = `
        <div class="d-flex align-items-center">
          <i class="bi bi-check-circle me-2"></i>
          <span>Feed entry deleted successfully!</span>
          <button type="button" class="btn-close ms-auto" onclick="this.parentElement.parentElement.remove()"></button>
        </div>
      `;
      document.body.appendChild(notification);
      
      // Auto-remove after 3 seconds
      setTimeout(() => {
        if (notification.parentNode) {
          document.body.removeChild(notification);
        }
      }, 3000);

    } catch (error) {
      console.error('Error deleting feed entry:', error);
      
      // Error notification
      const notification = document.createElement('div');
      notification.className = 'alert alert-danger position-fixed top-0 end-0 m-3';
      notification.style.zIndex = '9999';
      notification.style.minWidth = '300px';
      notification.innerHTML = `
        <div class="d-flex align-items-center">
          <i class="bi bi-exclamation-triangle me-2"></i>
          <span>Failed to delete feed entry</span>
          <button type="button" class="btn-close ms-auto" onclick="this.parentElement.parentElement.remove()"></button>
        </div>
      `;
      document.body.appendChild(notification);
      
      // Auto-remove after 5 seconds
      setTimeout(() => {
        if (notification.parentNode) {
          document.body.removeChild(notification);
        }
      }, 5000);
    } finally {
      setShowDeleteConfirmation(false);
      setFeedEntryToDelete(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDownloadFile = async (entry: FeedEntry) => {
    if (!entry.fileUrl || !entry.attachment) return;

    try {
      // Create a temporary download link that points directly to Firebase Storage URL
      const link = document.createElement('a');
      link.href = entry.fileUrl;
      link.download = entry.attachment.name;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Success notification
      const notification = document.createElement('div');
      notification.className = 'alert alert-success position-fixed top-0 end-0 m-3';
      notification.style.zIndex = '9999';
      notification.style.minWidth = '300px';
      notification.innerHTML = `
        <div class="d-flex align-items-center">
          <i class="bi bi-check-circle me-2"></i>
          <span>File download started!</span>
          <button type="button" class="btn-close ms-auto" onclick="this.parentElement.parentElement.remove()"></button>
        </div>
      `;
      document.body.appendChild(notification);
      
      // Auto-remove after 3 seconds
      setTimeout(() => {
        if (notification.parentNode) {
          document.body.removeChild(notification);
        }
      }, 3000);
      
    } catch (error) {
      console.error('Error downloading file:', error);
      
      // Fallback: open in new tab if direct download fails
      try {
        window.open(entry.fileUrl, '_blank');
        
        // Info notification for fallback
        const notification = document.createElement('div');
        notification.className = 'alert alert-info position-fixed top-0 end-0 m-3';
        notification.style.zIndex = '9999';
        notification.style.minWidth = '300px';
        notification.innerHTML = `
          <div class="d-flex align-items-center">
            <i class="bi bi-info-circle me-2"></i>
            <span>File opened in new tab</span>
            <button type="button" class="btn-close ms-auto" onclick="this.parentElement.parentElement.remove()"></button>
          </div>
        `;
        document.body.appendChild(notification);
        
        // Auto-remove after 3 seconds
        setTimeout(() => {
          if (notification.parentNode) {
            document.body.removeChild(notification);
          }
        }, 3000);
        
      } catch (fallbackError) {
        console.error('Fallback download also failed:', fallbackError);
        
        // Error notification
        const notification = document.createElement('div');
        notification.className = 'alert alert-danger position-fixed top-0 end-0 m-3';
        notification.style.zIndex = '9999';
        notification.style.minWidth = '300px';
        notification.innerHTML = `
          <div class="d-flex align-items-center">
            <i class="bi bi-exclamation-triangle me-2"></i>
            <span>Failed to download file</span>
            <button type="button" class="btn-close ms-auto" onclick="this.parentElement.parentElement.remove()"></button>
          </div>
        `;
        document.body.appendChild(notification);
        
        // Auto-remove after 5 seconds
        setTimeout(() => {
          if (notification.parentNode) {
            document.body.removeChild(notification);
          }
        }, 5000);
      }
    }
  };

  // Get task description from either field
  const getTaskDescription = (task: Task): string => {
    return task.description || task.task || '';
  };

  if (!task) return <></>;

  return (
    <>
      <div 
        className={`task-overlay ${show ? 'show' : ''}`}
        onClick={(e) => {
          // Only close if clicking on the overlay background, not on content
          if (e.target === e.currentTarget) {
            onHide();
          }
        }}
      >
        <div 
          className="task-overlay-content" 
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            width: '100%',
            height: '100%',
            padding: '0',
            overflow: 'hidden'
          }}
        >
          <div className="task-detail-card">
            {/* Header */}
            <div className="task-detail-header">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <div>
                    <h4 className="text-gray-900 fw-bold mb-1">{task.title}</h4>
                    <div className="text-muted fs-7">
                      Last updated {task.lastEdited ? formatDate(task.lastEdited) : 'Never'}
                      {task.feed && task.feed.length > 0 && (
                        <span className="badge badge-success ms-2">
                          {task.feed.length} update{task.feed.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button 
                  type="button" 
                  className="btn-close" 
                  aria-label="Close"
                  onClick={onHide}
                ></button>
              </div>
              
              {getTaskDescription(task) && (
                <div className="mt-4 p-3 rounded">
                  <div className="text-gray-700">{getTaskDescription(task)}</div>
                </div>
              )}
            </div>

            {/* Chat Feed */}
            <div className="task-detail-body">
              <div className="chat-container">
                <div className="chat-messages">
                  {task.feed && task.feed.length > 0 ? (
                    task.feed
                      .sort((a, b) => {
                        const getTimestamp = (timestamp: any) => {
                          if (!timestamp) return 0;
                          
                          try {
                            // Handle numeric timestamps
                            if (typeof timestamp === 'number') return timestamp;
                            
                            // Handle Firestore Timestamp objects
                            if (timestamp?.toDate && typeof timestamp.toDate === 'function') {
                              return timestamp.toDate().getTime();
                            }
                            
                            // Handle JavaScript Date objects
                            if (timestamp instanceof Date) {
                              return timestamp.getTime();
                            }
                            
                            // Handle string timestamps
                            if (typeof timestamp === 'string') {
                              const date = new Date(timestamp);
                              return isNaN(date.getTime()) ? 0 : date.getTime();
                            }
                            
                            // Handle objects with seconds property (Firestore format)
                            if (timestamp?.seconds) {
                              return timestamp.seconds * 1000;
                            }
                            
                            // Handle objects with _seconds property
                            if (timestamp?._seconds) {
                              return timestamp._seconds * 1000;
                            }
                            
                            return 0;
                          } catch (error) {
                            console.error('Error parsing timestamp for sorting:', error);
                            return 0;
                          }
                        };
                        
                        const timeA = getTimestamp(a.timestamp);
                        const timeB = getTimestamp(b.timestamp);
                        return timeA - timeB; // Oldest first for chat-like experience
                      })
                      .map((entry, index) => (
                        <div key={entry.id} className="chat-message mb-3">
                          <div className="d-flex align-items-start">
                            <div className="symbol symbol-35px me-3 flex-shrink-0">
                              <div className={`symbol-label ${entry.type === 'file' ? 'bg-info' : 'bg-success'}`}>
                                <i className={`bi ${entry.type === 'file' ? 'bi-paperclip' : 'bi-chat-text'} text-white fs-7`}></i>
                              </div>
                            </div>
                            <div className="flex-grow-1 min-w-0">
                              <div className="chat-bubble">
                                <div className="d-flex justify-content-between align-items-start">
                                  <div className="flex-grow-1">
                                    <div
                                      className="chat-content"
                                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(entry.content) }}
                                    />
                                    {entry.attachment && (
                                      <div className="attachment-preview mt-2">
                                        <div 
                                          className={`d-flex align-items-center p-2 bg-light-info rounded ${entry.fileUrl ? 'cursor-pointer' : ''}`}
                                          onClick={entry.fileUrl ? () => handleDownloadFile(entry) : undefined}
                                          title={entry.fileUrl ? "Click to download file" : "Legacy attachment - file not available for download"}
                                          style={{
                                            cursor: entry.fileUrl ? 'pointer' : 'not-allowed',
                                            opacity: entry.fileUrl ? 1 : 0.7,
                                            transition: 'opacity 0.2s, transform 0.1s',
                                            ...(entry.fileUrl && {
                                              ':hover': {
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                              }
                                            })
                                          }}
                                          onMouseEnter={(e) => {
                                            if (entry.fileUrl) {
                                              e.currentTarget.style.transform = 'translateY(-1px)';
                                              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            if (entry.fileUrl) {
                                              e.currentTarget.style.transform = 'translateY(0)';
                                              e.currentTarget.style.boxShadow = 'none';
                                            }
                                          }}
                                        >
                                          <i className="bi bi-file-earmark-text me-2 text-info"></i>
                                          <div className="flex-grow-1 min-w-0">
                                            <div className="fw-semibold text-truncate">{entry.attachment.name}</div>
                                            <small className="text-muted">
                                              {formatFileSize(entry.attachment.size)} • {entry.attachment.type}
                                              {!entry.fileUrl && (
                                                <span className="text-warning ms-2">
                                                  <i className="bi bi-exclamation-triangle me-1"></i>
                                                  Legacy attachment
                                                </span>
                                              )}
                                            </small>
                                          </div>
                                          {entry.fileUrl && (
                                            <i className="bi bi-download text-info ms-2" style={{ fontSize: '14px' }}></i>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    className="btn btn-sm btn-light-danger ms-2 flex-shrink-0"
                                    onClick={() => handleDeleteFeedEntry(entry.id)}
                                    title="Delete this update"
                                    style={{ opacity: 0.7 }}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                                  >
                                    <i className="bi bi-trash fs-8"></i>
                                  </button>
                                </div>
                              </div>
                              <div className="chat-timestamp text-muted fs-8 mt-1">
                                {formatDate(entry.timestamp)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                  ) : (
                    <div className="empty-chat text-center py-5">
                      <div className="symbol symbol-100px mx-auto mb-3">
                        <div className="symbol-label bg-light-success">
                          <i className="bi bi-chat-dots text-success fs-2x"></i>
                        </div>
                      </div>
                      <h5 className="text-gray-900 fw-bold mb-2">Start the conversation</h5>
                      <p className="text-muted">Add your first update to get started with this task</p>
                    </div>
                  )}
                  <div ref={feedEndRef} />
                </div>

                {/* Chat Input */}
                <div className="chat-input-container">
                  {!showInput ? (
                    <button
                      className="btn btn-success w-100"
                      onClick={() => setShowInput(true)}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      Add Update
                    </button>
                  ) : (
                                         <div 
                       className="chat-input-card" 
                       onClick={(e) => e.stopPropagation()}
                       onMouseDown={(e) => e.stopPropagation()}
                     >
                       <div 
                         className="input-section" 
                         onClick={(e) => e.stopPropagation()}
                         onMouseDown={(e) => e.stopPropagation()}
                       >
                         <div 
                           onClick={(e) => e.stopPropagation()}
                           onMouseDown={(e) => e.stopPropagation()}
                           onFocus={(e) => e.stopPropagation()}
                         >
                           <ReactQuill
                             ref={quillRef}
                             value={feedContent}
                             onChange={setFeedContent}
                             placeholder="Type your update..."
                             theme="snow"
                             modules={{
                               toolbar: [
                                 ['bold', 'italic', 'underline'],
                                 ['link'],
                                 [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                 ['clean']
                               ]
                             }}
                             formats={[
                               'bold', 'italic', 'underline',
                               'link', 'list', 'bullet'
                             ]}
                             style={{ 
                               minHeight: '120px',
                               backgroundColor: 'var(--bs-body-bg)',
                               zIndex: 1000
                             }}
                           />
                         </div>
                       </div>
                      
                      {/* Selected Files Preview */}
                      {selectedFiles.length > 0 && (
                        <div className="selected-files-preview">
                          <div className="d-flex flex-wrap gap-2">
                            {selectedFiles.map((file, index) => {
                              const progress = uploadProgress[`file_${index}`] || 0;
                              const isUploading = progress > 0 && progress < 100;
                              
                              return (
                                <div key={index} className="selected-file-item">
                                  <div className="d-flex align-items-center p-2 bg-light-info rounded">
                                    <i className="bi bi-file-earmark me-2 text-info"></i>
                                    <div className="flex-grow-1 min-w-0">
                                      <div className="fw-semibold text-truncate" style={{ maxWidth: '150px' }}>{file.name}</div>
                                      <small className="text-muted">
                                        {formatFileSize(file.size)}
                                        {isUploading && (
                                          <span className="text-success ms-2">
                                            <i className="bi bi-upload me-1"></i>
                                            {Math.round(progress)}%
                                          </span>
                                        )}
                                      </small>
                                      {isUploading && (
                                        <div className="progress mt-1" style={{ height: '2px' }}>
                                          <div 
                                            className="progress-bar bg-success" 
                                            role="progressbar" 
                                            style={{ width: `${progress}%` }}
                                            aria-valuenow={progress} 
                                            aria-valuemin={0} 
                                            aria-valuemax={100}
                                          ></div>
                                        </div>
                                      )}
                                    </div>
                                    <button 
                                      className="btn btn-sm btn-light-danger ms-2"
                                      onClick={() => removeSelectedFile(index)}
                                      title="Remove file"
                                      disabled={isUploading}
                                    >
                                      <i className="bi bi-x"></i>
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      <div className="input-actions">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <input
                              ref={fileInputRef}
                              type="file"
                              multiple
                              onChange={handleFileSelection}
                              style={{ display: 'none' }}
                            />
                            <button
                              className="btn btn-light-info btn-sm me-2"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={isUploading}
                            >
                              <i className="bi bi-paperclip me-1"></i>
                              Attach Files
                            </button>
                          </div>
                          
                          <div>
                            <button
                              className="btn btn-light btn-sm me-2"
                              onClick={() => {
                                setShowInput(false);
                                setFeedContent('');
                                setSelectedFiles([]);
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              className="btn btn-success btn-sm"
                              onClick={handleAddFeedEntry}
                              disabled={(!feedContent.trim() && selectedFiles.length === 0) || isSaving}
                            >
                              {isSaving ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-1"></span>
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-send me-1"></i>
                                  Send
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal 
        show={showDeleteConfirmation} 
        onHide={() => setShowDeleteConfirmation(false)}
        centered
        className="fade"
        style={{ zIndex: 9999 }}
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-gray-900">
            <i className="bi bi-exclamation-triangle text-warning me-2 fs-2"></i>
            Confirm Deletion
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center pt-0 pb-4">
          <div className="text-gray-600 fs-6 mb-3">
            Are you sure you want to delete this feed entry?
          </div>
          <div className="text-muted fs-7">
            This action cannot be undone and will permanently remove the update from this task.
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center pt-0">
          <Button 
            variant="light"
            className="me-3"
            onClick={() => setShowDeleteConfirmation(false)}
          >
            <i className="bi bi-x me-1"></i>
            Cancel
          </Button>
          <Button 
            variant="danger"
            onClick={confirmDeleteFeedEntry}
          >
            <i className="bi bi-trash me-1"></i>
            Delete Entry
          </Button>
        </Modal.Footer>
      </Modal>

      <style>
        {`
        .task-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 99999;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.3s ease, visibility 0.3s ease;
          backdrop-filter: blur(4px);
          pointer-events: none;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .task-overlay.show {
          opacity: 1;
          visibility: visible;
          pointer-events: auto;
        }
        
        .task-overlay-content {
          width: 100%;
          height: 100%;
          position: relative;
        }
        
        .task-detail-card {
          background-color: var(--bs-body-bg);
          border-radius: var(--bs-border-radius);
          box-shadow: var(--bs-box-shadow-lg);
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        
        .task-detail-header {
          padding: 1.5rem;
          border-bottom: 1px solid var(--bs-border-color);
          background-color: var(--bs-gray-100);
          border-radius: var(--bs-border-radius) var(--bs-border-radius) 0 0;
        }
        
        .task-detail-body {
          flex: 1;
          padding: 1.5rem;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        
        .chat-container {
          display: flex;
          flex-direction: column;
          height: 100%;
        }
        
        .chat-messages {
          flex: 1;
          overflow-y: auto;
          padding-right: 0.5rem;
          margin-bottom: 1rem;
        }
        
        .chat-message {
          animation: slideIn 0.3s ease;
        }
        
        .chat-bubble {
          background-color: var(--bs-gray-100);
          border-radius: 1rem;
          padding: 0.75rem 1rem;
          max-width: 85%;
          word-wrap: break-word;
        }
        
        .chat-content {
          color: var(--bs-gray-800);
        }
        
        .chat-timestamp {
          margin-left: 1rem;
        }
        
        .attachment-preview {
          max-width: 300px;
        }
        
        .chat-input-container {
          flex-shrink: 0;
        }
        
        .chat-input-card {
          background-color: var(--bs-body-bg);
          border: 2px solid var(--bs-success);
          border-radius: var(--bs-border-radius);
          padding: 1rem;
          animation: slideDown 0.3s ease;
        }
        
        .input-section {
          margin-bottom: 1rem;
        }
        
        .selected-files-preview {
          margin-bottom: 1rem;
          padding-top: 1rem;
          border-top: 1px solid var(--bs-border-color);
        }
        
        .selected-file-item {
          max-width: 250px;
        }
        
        .input-actions {
          padding-top: 1rem;
          border-top: 1px solid var(--bs-border-color);
        }
        
        .empty-chat {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* ReactQuill styling */
        .chat-input-card .ql-editor {
          min-height: 80px !important;
          max-height: 150px !important;
          overflow-y: auto !important;
          background-color: var(--graphite-surface-2, white) !important;
          color: var(--graphite-text, #333) !important;
          font-size: 14px !important;
          pointer-events: auto !important;
          user-select: text !important;
        }
        
        .chat-input-card .ql-editor:focus {
          outline: none !important;
          border: 2px solid var(--bs-primary) !important;
        }
        
        .chat-input-card .ql-editor.ql-blank::before {
          color: var(--graphite-text-3, #999) !important;
          font-style: normal !important;
          pointer-events: none !important;
        }
        
        .chat-input-card .ql-toolbar {
          border: 1px solid var(--graphite-sep, #ddd) !important;
          border-bottom: none !important;
          border-radius: var(--bs-border-radius-sm) var(--bs-border-radius-sm) 0 0 !important;
          background-color: var(--graphite-surface, #f8f9fa) !important;
          pointer-events: auto !important;
        }
        /* Toolbar glyphs (Quill draws them as SVG strokes/fills) — keep them
           visible on the now-dark toolbar under graphite/Onyx. */
        .chat-input-card .ql-toolbar .ql-stroke { stroke: var(--graphite-text-2, #444) !important; }
        .chat-input-card .ql-toolbar .ql-fill { fill: var(--graphite-text-2, #444) !important; }
        .chat-input-card .ql-toolbar .ql-picker { color: var(--graphite-text-2, #444) !important; }

        .chat-input-card .ql-container {
          border: 1px solid var(--graphite-sep, #ddd) !important;
          border-top: none !important;
          border-radius: 0 0 var(--bs-border-radius-sm) var(--bs-border-radius-sm) !important;
          background-color: var(--graphite-surface-2, white) !important;
          pointer-events: auto !important;
        }
        
        .chat-input-card .ql-toolbar .ql-formats {
          margin-right: 15px !important;
        }
        
        .chat-input-card .ql-toolbar button {
          padding: 5px !important;
          pointer-events: auto !important;
        }
        
        .chat-input-card .ql-toolbar button:hover {
          color: var(--bs-primary) !important;
        }
        
        /* Ensure ReactQuill components are interactive */
        .chat-input-card .quill {
          pointer-events: auto !important;
          position: relative !important;
          z-index: 1001 !important;
        }
        
        .chat-input-card .ql-formats button {
          pointer-events: auto !important;
        }
        
        /* Prevent focus stealing */
        .chat-input-card .ql-editor:focus {
          z-index: 1002 !important;
        }
        
        .input-section {
          position: relative !important;
          z-index: 1001 !important;
        }
        
        /* Scrollbar styling */
        .task-overlay-content::-webkit-scrollbar,
        .task-detail-body::-webkit-scrollbar,
        .task-feed::-webkit-scrollbar {
          width: 6px;
        }
        
        .task-overlay-content::-webkit-scrollbar-track,
        .task-detail-body::-webkit-scrollbar-track,
        .task-feed::-webkit-scrollbar-track {
          background: var(--bs-gray-200);
          border-radius: var(--bs-border-radius-pill);
        }
        
        .task-overlay-content::-webkit-scrollbar-thumb,
        .task-detail-body::-webkit-scrollbar-thumb,
        .task-feed::-webkit-scrollbar-thumb {
          background: var(--bs-gray-400);
          border-radius: var(--bs-border-radius-pill);
        }
        
        .task-overlay-content::-webkit-scrollbar-thumb:hover,
        .task-detail-body::-webkit-scrollbar-thumb:hover,
        .task-feed::-webkit-scrollbar-thumb:hover {
          background: var(--bs-gray-500);
        }
        
        /* Mobile responsive */
        @media (max-width: 768px) {
          .task-overlay {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: 100% !important;
            z-index: 1070 !important;
          }
          
          .task-overlay-content {
            width: 100% !important;
            height: 100% !important;
            padding: 0 !important;
          }
          
          .task-detail-card {
            width: 100% !important;
            height: 100% !important;
            border-radius: var(--bs-border-radius) !important;
            box-shadow: var(--bs-box-shadow-lg) !important;
          }
          
          .task-detail-header {
            padding: 1rem !important;
            border-radius: var(--bs-border-radius) var(--bs-border-radius) 0 0 !important;
          }
          
          .task-detail-body {
            padding: 1rem !important;
          }
          
          .chat-input-container {
            padding: 1rem !important;
          }
          
          .chat-input-card {
            margin: 0 !important;
          }
        }
        
        /* Theme variables handle dark mode automatically */
        `}
      </style>
    </>
  );
};

export default TaskOverlayModal; 